// BottomTabNavigation.jsx - Smart Auto-Hiding Bottom Navigation
// Fixed: Left-side compact floating button, no click conflicts
// Fixed: Scroll inside menu sheet no longer collapses expanded groups

import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  createContext,
  useContext,
} from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useI18n } from "../../hooks/useI18n";
import {
  HomeIcon,
  ViewGridIcon,
  XIcon,
  ChevronUpIcon,
  ChevronRightIcon,
  SearchIcon,
  SparklesIcon,
  CalendarIcon,
  CubeIcon,
  UserCircleIcon,
  LogoutIcon,
  MenuAlt2Icon,
  DotsVerticalIcon,
} from "@heroicons/react/outline";
import {
  HomeIcon as HomeIconSolid,
  ViewGridIcon as ViewGridSolid,
  SparklesIcon as SparklesSolid,
  CalendarIcon as CalendarSolid,
  CubeIcon as CubeSolid,
} from "@heroicons/react/solid";
import { getFilteredMenuConfig } from "../../config/menuConfig";

// ============================================
// CONSTANTS & CONFIGURATION
// ============================================

const BOTTOM_NAV_HEIGHT = 54;
export const BOTTOM_NAV_HEIGHT_VAR = "--bottom-nav-height";

// Auto-hide configuration
const AUTO_HIDE_CONFIG = {
  scrollThreshold: 8,
  hideDelay: 50,
  showDelay: 0,
  collisionCheckInterval: 200,
  idleTimeout: 5000,
  debugMode: false, // Set to true to debug
};

// Bottom tab configuration
const BOTTOM_TABS = [
  {
    id: "home",
    path: "/",
    translationKey: "menu.tabs.home",
    fallbackLabel: "Home",
    icon: HomeIcon,
    iconActive: HomeIconSolid,
    permissions: [],
    order: 1,
  },
  {
    id: "products",
    path: "/products",
    translationKey: "menu.tabs.products",
    fallbackLabel: "Products",
    icon: CubeIcon,
    iconActive: CubeSolid,
    permissions: ["VIEW_PRODUCTS", "ADMIN"],
    order: 2,
  },
  {
    id: "menu",
    isMenu: true,
    translationKey: "menu.tabs.menu",
    fallbackLabel: "Menu",
    icon: ViewGridIcon,
    iconActive: ViewGridSolid,
    permissions: [],
    order: 3,
  },
  {
    id: "appointments",
    path: "/appointments",
    translationKey: "menu.tabs.appointments",
    fallbackLabel: "Bookings",
    icon: CalendarIcon,
    iconActive: CalendarSolid,
    permissions: ["VIEW_APPOINTMENTS", "ADMIN"],
    order: 4,
  },
  {
    id: "plans",
    path: "/jewellery/dashboard",
    translationKey: "menu.tabs.plans",
    fallbackLabel: "Plans",
    icon: SparklesIcon,
    iconActive: SparklesSolid,
    permissions: ["VIEW_PLANS", "ADMIN"],
    order: 5,
  },
];

// ============================================
// CONTEXT
// ============================================

const BottomNavContext = createContext({
  isVisible: true,
  isHidden: false,
  hasCollision: false,
  show: () => {},
  hide: () => {},
});

export const useBottomNav = () => useContext(BottomNavContext);

// ============================================
// HOOKS
// ============================================

const useBreakpoint = () => {
  const [breakpoint, setBreakpoint] = useState(() => {
    if (typeof window === "undefined") return "desktop";
    const width = window.innerWidth;
    if (width < 640) return "mobile";
    if (width < 768) return "mobile-lg";
    if (width < 1024) return "tablet";
    return "desktop";
  });

  useEffect(() => {
    const checkBreakpoint = () => {
      const width = window.innerWidth;
      if (width < 640) setBreakpoint("mobile");
      else if (width < 768) setBreakpoint("mobile-lg");
      else if (width < 1024) setBreakpoint("tablet");
      else setBreakpoint("desktop");
    };

    window.addEventListener("resize", checkBreakpoint);
    return () => window.removeEventListener("resize", checkBreakpoint);
  }, []);

  return {
    isMobile: breakpoint === "mobile" || breakpoint === "mobile-lg",
    isTablet: breakpoint === "tablet",
    isDesktop: breakpoint === "desktop",
    breakpoint,
  };
};

// ============================================
// COLLISION DETECTION
// ============================================

const useBottomCollisionDetection = (isDesktop, isMenuOpen) => {
  const [hasCollision, setHasCollision] = useState(false);

  useEffect(() => {
    if (isDesktop || isMenuOpen) {
      setHasCollision(false);
      return;
    }

    const checkCollisions = () => {
      const viewportHeight = window.innerHeight;
      const navTop = viewportHeight - BOTTOM_NAV_HEIGHT - 10;

      // Look for fixed elements at the bottom
      const candidates = document.querySelectorAll(
        'button, [role="button"], footer, .fixed, [class*="fixed"]'
      );

      let foundCollision = false;

      for (const el of candidates) {
        // Skip our elements
        if (
          el.closest("[data-bottom-nav]") ||
          el.closest("[data-menu-sheet]") ||
          el.closest("[data-nav-fab]")
        ) {
          continue;
        }

        const style = window.getComputedStyle(el);

        if (style.position === "fixed" || style.position === "sticky") {
          const rect = el.getBoundingClientRect();

          // Check if in bottom zone and visible
          if (
            rect.bottom > navTop &&
            rect.top < viewportHeight &&
            rect.height > 30 &&
            rect.width > 80 &&
            style.visibility !== "hidden" &&
            style.display !== "none" &&
            parseFloat(style.opacity) > 0.1
          ) {
            foundCollision = true;
            if (AUTO_HIDE_CONFIG.debugMode) {
              console.log("🔴 Collision:", el.className?.substring(0, 30));
            }
            break;
          }
        }
      }

      setHasCollision(foundCollision);
    };

    checkCollisions();
    const intervalId = setInterval(
      checkCollisions,
      AUTO_HIDE_CONFIG.collisionCheckInterval
    );

    const observer = new MutationObserver(() => {
      requestAnimationFrame(checkCollisions);
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["style", "class"],
    });

    return () => {
      clearInterval(intervalId);
      observer.disconnect();
    };
  }, [isDesktop, isMenuOpen]);

  return { hasCollision };
};

// ============================================
// SCROLL DIRECTION DETECTION
// Fixed: Now ignores scroll events inside menu sheet
// ============================================

const useScrollDirection = (isDesktop, isMenuOpen) => {
  const [scrollDirection, setScrollDirection] = useState("up");
  const [isScrolling, setIsScrolling] = useState(false);
  const lastScrollY = useRef(0);
  const scrollTimeout = useRef(null);
  const ticking = useRef(false);

  useEffect(() => {
    if (isDesktop) return;

    // Helper to check if event originated inside menu sheet
    const isInsideMenuSheet = (target) => {
      return target?.closest?.("[data-menu-sheet]") !== null;
    };

    const handleScroll = (e) => {
      // IMPORTANT: Ignore scroll events from inside the menu sheet
      if (isInsideMenuSheet(e.target)) return;

      let currentScrollY;

      if (e.target === document || e.target === document.documentElement) {
        currentScrollY = window.scrollY || document.documentElement.scrollTop;
      } else if (e.target.scrollTop !== undefined) {
        currentScrollY = e.target.scrollTop;
      } else {
        currentScrollY = window.scrollY;
      }

      if (!ticking.current) {
        requestAnimationFrame(() => {
          const diff = currentScrollY - lastScrollY.current;

          if (Math.abs(diff) > AUTO_HIDE_CONFIG.scrollThreshold) {
            setScrollDirection(diff > 0 ? "down" : "up");
            lastScrollY.current = currentScrollY;
          }

          setIsScrolling(true);
          if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
          scrollTimeout.current = setTimeout(() => setIsScrolling(false), 150);

          ticking.current = false;
        });
        ticking.current = true;
      }
    };

    // Touch-based scroll detection
    let touchStartY = 0;
    let touchTarget = null;

    const handleTouchStart = (e) => {
      touchTarget = e.target;
      // IMPORTANT: Don't track touches that start inside menu sheet
      if (isInsideMenuSheet(touchTarget)) return;
      touchStartY = e.touches[0].clientY;
    };

    const handleTouchMove = (e) => {
      // IMPORTANT: Ignore touch moves inside menu sheet
      if (isInsideMenuSheet(e.target) || isInsideMenuSheet(touchTarget)) return;

      const diff = touchStartY - e.touches[0].clientY;
      if (Math.abs(diff) > 10) {
        setScrollDirection(diff > 0 ? "down" : "up");
        setIsScrolling(true);
        if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
        scrollTimeout.current = setTimeout(() => setIsScrolling(false), 150);
        touchStartY = e.touches[0].clientY;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    document.addEventListener("scroll", handleScroll, {
      passive: true,
      capture: true,
    });
    document.addEventListener("touchstart", handleTouchStart, {
      passive: true,
    });
    document.addEventListener("touchmove", handleTouchMove, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("scroll", handleScroll, { capture: true });
      document.removeEventListener("touchstart", handleTouchStart);
      document.removeEventListener("touchmove", handleTouchMove);
      if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
    };
  }, [isDesktop, isMenuOpen]);

  return { scrollDirection, isScrolling };
};

// ============================================
// AUTO-HIDE LOGIC
// ============================================

const useAutoHide = (
  isDesktop,
  hasCollision,
  scrollDirection,
  isScrolling,
  isMenuOpen
) => {
  const [isHidden, setIsHidden] = useState(false);
  const [isManuallyShown, setIsManuallyShown] = useState(false);
  const idleTimeout = useRef(null);
  const hideTimeout = useRef(null);
  const location = useLocation();

  const show = useCallback(() => {
    setIsHidden(false);
    setIsManuallyShown(true);
    if (idleTimeout.current) clearTimeout(idleTimeout.current);
    idleTimeout.current = setTimeout(
      () => setIsManuallyShown(false),
      AUTO_HIDE_CONFIG.idleTimeout
    );
  }, []);

  const hide = useCallback(() => {
    setIsHidden(true);
    setIsManuallyShown(false);
  }, []);

  useEffect(() => {
    if (isMenuOpen) setIsHidden(false);
  }, [isMenuOpen]);

  useEffect(() => {
    if (isDesktop || isMenuOpen) {
      setIsHidden(false);
      return;
    }

    if (isManuallyShown) return;

    // Collision = hide immediately
    if (hasCollision) {
      if (hideTimeout.current) clearTimeout(hideTimeout.current);
      setIsHidden(true);
      return;
    }

    // Scroll down = hide
    if (scrollDirection === "down" && isScrolling) {
      if (hideTimeout.current) clearTimeout(hideTimeout.current);
      hideTimeout.current = setTimeout(
        () => setIsHidden(true),
        AUTO_HIDE_CONFIG.hideDelay
      );
    } else if (scrollDirection === "up") {
      if (hideTimeout.current) clearTimeout(hideTimeout.current);
      setIsHidden(false);
    }

    return () => {
      if (hideTimeout.current) clearTimeout(hideTimeout.current);
    };
  }, [
    isDesktop,
    hasCollision,
    scrollDirection,
    isScrolling,
    isManuallyShown,
    isMenuOpen,
  ]);

  // Reset on route change
  useEffect(() => {
    setIsHidden(false);
    setIsManuallyShown(false);
  }, [location.pathname]);

  return { isHidden, show, hide };
};

// ============================================
// BOTTOM NAV HEIGHT HOOK
// ============================================

export const useBottomNavHeight = () => {
  const [height, setHeight] = useState(BOTTOM_NAV_HEIGHT);
  const { isDesktop } = useBreakpoint();

  useEffect(() => {
    if (isDesktop) {
      setHeight(0);
      document.documentElement.style.setProperty(BOTTOM_NAV_HEIGHT_VAR, "0px");
      return;
    }

    const calculateHeight = () => {
      const safeArea = parseInt(
        getComputedStyle(document.documentElement).getPropertyValue(
          "env(safe-area-inset-bottom)"
        ) || "0",
        10
      );
      const total = BOTTOM_NAV_HEIGHT + safeArea;
      setHeight(total);
      document.documentElement.style.setProperty(
        BOTTOM_NAV_HEIGHT_VAR,
        `${total}px`
      );
    };

    calculateHeight();
    window.addEventListener("resize", calculateHeight);
    return () => window.removeEventListener("resize", calculateHeight);
  }, [isDesktop]);

  return height;
};

// ============================================
// COMPACT FLOATING MENU BUTTON (LEFT SIDE)
// ============================================

const FloatingMenuButton = ({ onClick, isVisible }) => {
  if (!isVisible) return null;

  return (
    <button
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick();
      }}
      data-nav-fab="true"
      className="fixed z-50 left-3 flex items-center justify-center
                 w-12 h-12 rounded-full 
                 bg-primary text-white shadow-xl
                 active:scale-90 transition-transform duration-200"
      style={{
        bottom: `calc(env(safe-area-inset-bottom, 0px) + 12px)`,
        animation: "popIn 0.25s ease-out",
        boxShadow:
          "0 4px 20px rgba(0,0,0,0.25), 0 0 0 3px rgba(255,255,255,0.9)",
      }}
      aria-label="Open menu"
    >
      <MenuAlt2Icon className="w-6 h-6" />

      {/* Notification dot */}
      <span
        className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-danger rounded-full border-2 border-white"
        style={{ animation: "pulse 2s infinite" }}
      />
    </button>
  );
};

// ============================================
// MENU SHEET
// ============================================

const MenuSheet = ({
  isOpen,
  onClose,
  menuConfig,
  t,
  isActiveLink,
  onLogout,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedGroups, setExpandedGroups] = useState({});
  const sheetRef = useRef(null);
  const startY = useRef(0);
  const currentY = useRef(0);
  const isDragging = useRef(false);

  useEffect(() => {
    if (menuConfig) {
      const initial = {};
      menuConfig.forEach((g) => {
        initial[g.id] = false;
      });
      setExpandedGroups(initial);
    }
  }, [menuConfig]);

  useEffect(() => {
    if (!isOpen) setSearchQuery("");
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
      document.body.style.top = `-${scrollY}px`;
    } else {
      const scrollY = document.body.style.top;
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
      document.body.style.top = "";
      if (scrollY) window.scrollTo(0, parseInt(scrollY || "0") * -1);
    }
    return () => {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
      document.body.style.top = "";
    };
  }, [isOpen]);

  const handleTouchStart = (e) => {
    // Only enable drag-to-close on the drag handle area, not the content
    const target = e.target;
    const isDragHandle = target.closest(".drag-handle-area");
    if (!isDragHandle) return;

    startY.current = e.touches[0].clientY;
    isDragging.current = true;
  };

  const handleTouchMove = (e) => {
    if (!isDragging.current) return;
    currentY.current = e.touches[0].clientY;
    const diff = currentY.current - startY.current;
    if (diff > 0 && sheetRef.current) {
      sheetRef.current.style.transform = `translateY(${diff}px)`;
      sheetRef.current.style.transition = "none";
    }
  };

  const handleTouchEnd = () => {
    if (!isDragging.current) return;
    isDragging.current = false;
    const diff = currentY.current - startY.current;
    if (sheetRef.current) {
      sheetRef.current.style.transition = "transform 0.3s ease-out";
      if (diff > 100) {
        sheetRef.current.style.transform = "translateY(100%)";
        setTimeout(onClose, 300);
      } else {
        sheetRef.current.style.transform = "";
      }
    }
    // Reset values
    startY.current = 0;
    currentY.current = 0;
  };

  const getFilteredGroups = () => {
    if (!searchQuery || !menuConfig) return menuConfig || [];
    const q = searchQuery.toLowerCase();
    return menuConfig
      .map((g) => ({
        ...g,
        items: g.items.filter((i) =>
          (t(i.translationKey) || "").toLowerCase().includes(q)
        ),
      }))
      .filter((g) => g.items.length > 0);
  };

  const toggleGroup = (id) => {
    setExpandedGroups((p) => ({ ...p, [id]: !p[id] }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] md:hidden" data-menu-sheet="true">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        style={{ animation: "fadeIn 0.2s ease-out" }}
      />

      <div
        ref={sheetRef}
        className="absolute bottom-0 left-0 right-0 bg-background rounded-t-3xl shadow-2xl overflow-hidden flex flex-col"
        style={{ maxHeight: "85vh", animation: "slideUp 0.3s ease-out" }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Drag handle - only this area triggers swipe-to-close */}
        <div className="drag-handle-area flex justify-center py-3 cursor-grab active:cursor-grabbing">
          <div className="w-10 h-1 bg-neutral-dark/30 rounded-full" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 pb-3 border-b border-neutral-light">
          <h2 className="text-xl font-bold text-text">
            {t("common.menu") || "Menu"}
          </h2>
          <button
            onClick={onClose}
            className="p-2.5 rounded-xl bg-neutral-light active:scale-95"
          >
            <XIcon className="w-5 h-5 text-neutral-dark" />
          </button>
        </div>

        {/* Search */}
        <div className="px-4 py-3">
          <div className="relative">
            <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-dark/50" />
            <input
              type="text"
              placeholder={t("common.search_menu") || "Search..."}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-neutral-light rounded-xl text-text placeholder-neutral-dark/50 focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
        </div>

        {/* Menu items - scrollable content */}
        <div
          className="menu-sheet-content flex-1 overflow-y-auto overscroll-contain px-3"
          style={{
            minHeight: 0,
            WebkitOverflowScrolling: "touch",
            paddingBottom:
              "max(16px, calc(env(safe-area-inset-bottom) + 16px))",
          }}
        >
          {getFilteredGroups().length === 0 ? (
            <div className="text-center py-12 text-neutral-dark">
              {t("common.no_results") || "No results"}
            </div>
          ) : (
            getFilteredGroups().map((group) => (
              <div key={group.id} className="mb-2">
                <button
                  onClick={() => toggleGroup(group.id)}
                  className={`w-full flex items-center justify-between p-4 rounded-2xl transition-all active:scale-[0.98]
                    ${
                      expandedGroups[group.id]
                        ? "bg-primary text-white"
                        : "bg-neutral-light text-text"
                    }`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`p-2 rounded-xl ${
                        expandedGroups[group.id]
                          ? "bg-white/20"
                          : "bg-primary/10"
                      }`}
                    >
                      <group.icon
                        className={`w-5 h-5 ${
                          expandedGroups[group.id]
                            ? "text-white"
                            : "text-primary"
                        }`}
                      />
                    </div>
                    <span className="font-semibold">
                      {t(`${group.translationKey}.label`) || group.id}
                    </span>
                  </div>
                  <ChevronUpIcon
                    className={`w-5 h-5 transition-transform ${
                      expandedGroups[group.id] ? "" : "rotate-180"
                    }`}
                  />
                </button>

                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    expandedGroups[group.id] ? "max-h-[500px] mt-2" : "max-h-0"
                  }`}
                >
                  <div className="space-y-1 pl-2">
                    {group.items.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={onClose}
                        className={`flex items-center gap-3 p-3 rounded-xl transition-all active:scale-[0.98]
                          ${
                            isActiveLink(item.path)
                              ? "bg-primary/10 text-primary"
                              : "text-text hover:bg-neutral-light"
                          }`}
                      >
                        <item.icon
                          className={`w-5 h-5 ${
                            isActiveLink(item.path)
                              ? "text-primary"
                              : "text-neutral-dark"
                          }`}
                        />
                        <span
                          className={
                            isActiveLink(item.path) ? "font-medium" : ""
                          }
                        >
                          {t(item.translationKey) || item.path}
                        </span>
                        <ChevronRightIcon className="w-4 h-4 ml-auto text-neutral-dark/50" />
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        @keyframes slideUp {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

// ============================================
// TAB ITEM
// ============================================

const TabItem = ({ tab, isActive, isMenuButton, onClick, t, badge }) => {
  const Icon = isActive ? tab.iconActive : tab.icon;
  const label = t(tab.translationKey) || tab.fallbackLabel;

  if (isMenuButton) {
    return (
      <button
        onClick={onClick}
        className="relative flex flex-col items-center py-0.5 px-2 min-w-[48px]"
        aria-label={label}
      >
        <div
          className={`flex items-center justify-center w-11 h-11 -mt-5 rounded-xl shadow-lg transition-all
          ${isActive ? "bg-primary scale-105" : "bg-primary hover:scale-105"}`}
        >
          <Icon className="w-5 h-5 text-white" />
        </div>
        <span
          className={`mt-1 text-[9px] font-semibold ${
            isActive ? "text-primary" : "text-neutral-dark"
          }`}
        >
          {label}
        </span>
      </button>
    );
  }

  return (
    <button
      onClick={onClick}
      className="relative flex flex-col items-center py-1 px-1.5 min-w-[48px] group"
      aria-label={label}
    >
      <div
        className={`absolute top-0 left-1/2 -translate-x-1/2 h-[2px] rounded-full transition-all ${
          isActive ? "w-5 bg-primary" : "w-0"
        }`}
      />
      <div
        className={`relative p-1.5 rounded-lg transition-all ${
          isActive ? "bg-primary/10" : ""
        }`}
      >
        <Icon
          className={`w-5 h-5 ${
            isActive ? "text-primary" : "text-neutral-dark"
          }`}
        />
        {badge && (
          <span className="absolute -top-1 -right-1 min-w-[14px] h-[14px] text-[8px] font-bold bg-danger text-white rounded-full flex items-center justify-center">
            {badge > 99 ? "99+" : badge}
          </span>
        )}
      </div>
      <span
        className={`text-[9px] font-medium ${
          isActive ? "text-primary font-semibold" : "text-neutral-dark"
        }`}
      >
        {label}
      </span>
    </button>
  );
};

// ============================================
// SPACER
// ============================================

export const BottomNavSpacer = () => {
  const { isDesktop } = useBreakpoint();
  const { isHidden } = useBottomNav();

  if (isDesktop) return null;

  return (
    <div
      className="w-full pointer-events-none md:hidden transition-all duration-300"
      style={{
        height: isHidden
          ? 0
          : `var(${BOTTOM_NAV_HEIGHT_VAR}, ${BOTTOM_NAV_HEIGHT}px)`,
      }}
    />
  );
};

// ============================================
// DEBUG (remove in production)
// ============================================

const Debug = ({ isHidden, hasCollision, scrollDir }) => {
  if (!AUTO_HIDE_CONFIG.debugMode) return null;
  return (
    <div className="fixed top-16 left-2 z-[200] bg-black/80 text-white text-[10px] p-2 rounded font-mono">
      <div>Hidden: {isHidden ? "✅" : "❌"}</div>
      <div>Collision: {hasCollision ? "🔴" : "🟢"}</div>
      <div>Scroll: {scrollDir}</div>
    </div>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================

const BottomTabNavigation = ({ featuresConfig = {} }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { t } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const { isDesktop } = useBreakpoint();
  const { isRootAdmin, permissions = [] } = useSelector((state) => state.auth);

  const bottomNavHeight = useBottomNavHeight();
  const { hasCollision } = useBottomCollisionDetection(isDesktop, isMenuOpen);
  // Pass isMenuOpen to useScrollDirection so it can be aware of menu state
  const { scrollDirection, isScrolling } = useScrollDirection(
    isDesktop,
    isMenuOpen
  );
  const { isHidden, show, hide } = useAutoHide(
    isDesktop,
    hasCollision,
    scrollDirection,
    isScrolling,
    isMenuOpen
  );

  const menuConfig = getFilteredMenuConfig(
    permissions,
    isRootAdmin,
    featuresConfig
  );
  const [badges] = useState({ orders: 3, appointments: 0 });

  const isActiveLink = useCallback(
    (path) => {
      if (path === "/") return location.pathname === "/";
      return location.pathname.startsWith(path);
    },
    [location.pathname]
  );

  const visibleTabs = BOTTOM_TABS.filter(
    (tab) =>
      !tab.permissions?.length ||
      tab.permissions.some((p) => permissions.includes(p) || isRootAdmin)
  ).sort((a, b) => a.order - b.order);

  const handleTabClick = useCallback(
    (tab) => {
      if (navigator.vibrate) navigator.vibrate(8);
      if (tab.isMenu) setIsMenuOpen(true);
      else if (tab.path) navigate(tab.path);
    },
    [navigate]
  );

  const handleLogout = useCallback(() => {
    if (window.confirm(t("header.logout_confirm") || "Logout?")) {
      console.log("Logout");
    }
  }, [t]);

  const handleFloatingMenuClick = useCallback(() => {
    setIsMenuOpen(true);
  }, []);

  const contextValue = useMemo(
    () => ({
      isVisible: !isHidden,
      isHidden,
      hasCollision,
      show,
      hide,
    }),
    [isHidden, hasCollision, show, hide]
  );

  if (isDesktop) return null;

  return (
    <BottomNavContext.Provider value={contextValue}>
      <Debug
        isHidden={isHidden}
        hasCollision={hasCollision}
        scrollDir={scrollDirection}
      />

      {/* Main Nav Bar */}
      <nav
        data-bottom-nav="true"
        className="fixed bottom-0 left-0 right-0 z-10 md:hidden transition-all duration-300"
        style={{
          height: `${BOTTOM_NAV_HEIGHT}px`,
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
          transform: isHidden
            ? "translateY(calc(100% + env(safe-area-inset-bottom, 20px)))"
            : "translateY(0)",
          opacity: isHidden ? 0 : 1,
          pointerEvents: isHidden ? "none" : "auto",
        }}
      >
        <div
          className="absolute inset-0 border-t border-neutral-light/80"
          style={{
            background: "rgba(255,255,255,0.95)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
          }}
        />
        <div className="relative flex items-end justify-around px-1 pb-0.5 h-full">
          {visibleTabs.map((tab) => (
            <TabItem
              key={tab.id}
              tab={tab}
              isActive={tab.isMenu ? isMenuOpen : isActiveLink(tab.path)}
              isMenuButton={tab.isMenu}
              onClick={() => handleTabClick(tab)}
              t={t}
              badge={tab.badge && badges[tab.id]}
            />
          ))}
        </div>
      </nav>

      {/* Floating Menu Button (LEFT side, compact) */}
      <FloatingMenuButton
        onClick={handleFloatingMenuClick}
        isVisible={isHidden && !isMenuOpen}
      />

      {/* Menu Sheet */}
      <MenuSheet
        isOpen={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
        menuConfig={menuConfig}
        t={t}
        isActiveLink={isActiveLink}
        onLogout={handleLogout}
      />

      <style jsx global>{`
        @keyframes popIn {
          0% {
            opacity: 0;
            transform: scale(0.5);
          }
          70% {
            transform: scale(1.1);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }
        @keyframes pulse {
          0%,
          100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
      `}</style>
    </BottomNavContext.Provider>
  );
};

export default BottomTabNavigation;
export { useBreakpoint, BOTTOM_NAV_HEIGHT };
